import React from 'react';
import PropTypes from 'prop-types';
import { Card, Divider, Expanded, RatingValue, Row, Typography } from '@tempmee/react';
import Styles from './ReviewCard.module.scss';

/**
 * The frame both Rating-tab review cards share: a bordered surface, a heading
 * of "name — ★ score", and a rule beneath it.
 *
 * The two cards' *bodies* are separate components, because below this rule they
 * have nothing in common — different rows, different orientations, different
 * data sources, different states. A single card parameterised over both is what
 * produced the file this replaces, where the office side carried a dead
 * "Rating" row because one `ratingItems` array had to serve both.
 *
 * What is genuinely shared is this frame, and it is worth extracting for one
 * reason above the others: the star guard below. Copy it into two files and it
 * is the line that gets forgotten in one of them.
 *
 * Padding is `p-5` on the `Card` rather than on an inner `Box`. `Card` bakes in
 * `p-2`, so the usual inner-padding idiom would land on 28px instead of the
 * frame's 20px — and `cn` is tailwind-merge, so `p-5` deterministically
 * *replaces* `p-2`. Putting the padding in the scss module instead would be a
 * coin flip: `.p-2` and `.reviewCard` have the same specificity, so the winner
 * would depend on stylesheet order, which `src/index.js` warns is load-bearing.
 */
export const ReviewCard = ({ label, name, rating, children }) => (
  <Card
    outline
    // Both cards can show identical copy — "No review submitted", "N/A" — so
    // without a name on each, a test asserting on that copy would have to index
    // into `getAllByText`, which breaks the moment the DOM order changes.
    role="group"
    aria-label={label}
    // Figma's 20px padding and 8px inner gap, plus `w-auto` to drop Card's
    // own `w-full` and leave the width to the parent grid track and the
    // module's min/max bounds.
    //
    // These go through `className` rather than the scss module because `Card`
    // bakes in `p-2` and `w-full`, and `cn` is tailwind-merge, so a class
    // *replaces* those deterministically. A module rule would be a coin flip:
    // `.p-2` and `.reviewCard` have equal specificity, so the winner would
    // depend on stylesheet injection order, which `src/index.js` warns is
    // load-bearing.
    className={`w-auto p-5 gap-2 ${Styles.reviewCard}`}
  >
    <Row justify="between" align="center" gap={2}>
      {/* min-w-0 lets a long office name wrap instead of pushing the score
          out of the card. */}
      <Expanded className="min-w-0">
        <Typography variant="bodyLarge">{name}</Typography>
      </Expanded>

      {/* No score, no star, and this guard is load-bearing rather than
          cosmetic on both halves.

          `Number.isFinite` because `RatingValue` calls `value.toFixed(1)` on a
          prop it types as a required number — a null rating throws a
          TypeError, inside a tab with no error boundary.

          `> 0` because a star rating is 1–5, so a 0 is missing data rather
          than a score, and "★ 0.0" reads as a rating nobody gave. This is also
          what the card being replaced did, by gating on truthiness. The
          mappers still report a 0 faithfully; deciding it isn't worth drawing
          is this component's call. */}
      {Number.isFinite(rating) && rating > 0 ? <RatingValue value={rating} /> : null}
    </Row>

    <Divider />

    {children}
  </Card>
);

ReviewCard.propTypes = {
  /** The card's accessible name, e.g. "Office review". Not rendered. */
  label: PropTypes.string.isRequired,
  /** The office's or professional's name; the visible heading. */
  name: PropTypes.string,
  /** The score beside the name. Anything non-finite renders no star at all. */
  rating: PropTypes.number,
  children: PropTypes.node.isRequired,
};

ReviewCard.defaultProps = {
  name: '',
  rating: null,
};

export default ReviewCard;
